import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FaBars, FaTimes } from "react-icons/fa";
import { useSelector } from "react-redux";
import { PhoneCall, ChevronDown } from "lucide-react";
import RegisterModal from "./RegisterModal";
import UserProfileMenu from "./UserProfileMenu";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showInstaToolsDropdown, setShowInstaToolsDropdown] = useState(false);
  const [mobileInstaToolsOpen, setMobileInstaToolsOpen] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const instaToolsDropdownRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Get user data from Redux store
  const user = useSelector((state) => state.user.data);
  const company = useSelector((state) => state.company.company);
  
  // Check if user is actually authenticated with valid token
  const hasValidToken = !!localStorage.getItem('token');
  // const isLoggedIn = hasValidToken && (!!user || !!company);
  const isLoggedIn = hasValidToken && (user?._id || company?._id);
  const isInfluencerDashboard = location.pathname.startsWith("/influencer/dashboard");
  const isCompanyDashboard = location.pathname.startsWith("/company/dashboard");
  const showDesktopProfileMenu = isLoggedIn && !isInfluencerDashboard && !isCompanyDashboard;

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Insta Tools dropdown
      if (
        instaToolsDropdownRef.current &&
        !instaToolsDropdownRef.current.contains(event.target)
      ) {
        setShowInstaToolsDropdown(false);
      }
    };
  
    document.addEventListener("click", handleClickOutside);
  
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setMobileInstaToolsOpen(false);
    setShowInstaToolsDropdown(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleRegisterClick = () => {
    setShowRegisterModal(true);
  };

  const handleRegisterAs = (type) => {
    setShowRegisterModal(false);
    if (type === 'influencer') {
      navigate("/influencer/login");
    } else {
      navigate("/company/login");
    }
  };

  // Navigation links data
  const navLinks = [
    { path: "/", label: "Home" },
    // { path: "/influencer", label: "Influencer" },
    { path: "/marketplace", label: "Marketplace" },
    { path: "/campaign", label: "Campaign" },
    { path: "/brands", label: "Brands" },
    { path: "/our-work", label: "What We Do?" },
    { path: "/our-story", label: "Our Story" },
    {
      path: "/insta-tools",
      label: "Insta Tools",
      children: [
        { path: "/insta-tools/pricing", label: "Pricing" },
        { path: "/insta-tools/info", label: "Info" },
      ],
    },
  ];

  const isInstaToolsActive = location.pathname.startsWith("/insta-tools");

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 flex h-20 items-center justify-between bg-black px-5 shadow-md">
      {/* Logo */}
      <Link to="/" className="text-2xl lg:text-3xl font-bold text-orange-500">
        Infuber
      </Link>

      {/* Desktop Navigation (Large screens only) */}
      <ul className="hidden lg:flex space-x-6 text-white">
        {navLinks.map((link) => (
          <li key={link.path}>
            {link.children ? (
              <div
                className={`relative group ${showInstaToolsDropdown ? "is-open" : ""}`}
                ref={instaToolsDropdownRef}
              >
                <button
                  type="button"
                  className={`inline-flex items-center gap-1 hover:text-orange-500 transition-colors ${
                    isInstaToolsActive ? "text-orange-500" : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowInstaToolsDropdown((prev) => !prev);
                  }}
                  aria-expanded={showInstaToolsDropdown}
                  aria-haspopup="true"
                >
                  {link.label}
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 group-hover:rotate-180 ${
                      showInstaToolsDropdown ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {/* pt-2 bridges the gap so the cursor can reach submenu items without closing */}
                <div
                  className="absolute top-full left-0 pt-2 w-44 text-gray-600 invisible opacity-0 translate-y-1 pointer-events-none transition-all duration-200 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto group-[.is-open]:visible group-[.is-open]:opacity-100 group-[.is-open]:translate-y-0 group-[.is-open]:pointer-events-auto z-[60]"
                >
                  <div className="bg-white rounded-lg shadow-xl py-1 border border-gray-200 overflow-hidden text-gray-600">
                    {link.children.map((child) => {
                      const isChildActive = location.pathname === child.path;
                      return (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`block px-4 py-2.5 text-sm transition-colors hover:bg-orange-50 hover:text-orange-600 ${
                            isChildActive
                              ? "font-semibold text-gray-900"
                              : "text-gray-600"
                          }`}
                          onClick={() => setShowInstaToolsDropdown(false)}
                        >
                          {child.label}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                to={link.path}
                className={`hover:text-orange-500 transition-colors ${
                  location.pathname === link.path ? "text-orange-500" : ""
                }`}
              >
                {link.label}
              </Link>
            )}
          </li>
        ))}
      </ul>

      {/* Desktop Login/User Profile & Contact Button (Large screens only) */}
      <div className="hidden lg:flex space-x-4">
        <button
          className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-4 py-2 text-white font-semibold tracking-wide hover:bg-orange-600 transition-colors"
          onClick={() => navigate("/contact-details")}
        >
          <span className="flex h-6 w-6 items-center justify-center rounded-full border border-white/70">
            <PhoneCall className="h-4 w-4" />
          </span>
          Contact Us
        </button>

        {showDesktopProfileMenu ? (
          <UserProfileMenu />
        ) : (
          !isLoggedIn && (
            <button
              className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-4 py-2 text-white font-semibold tracking-wide hover:bg-orange-600 transition-colors"
              onClick={handleRegisterClick}
            >
              Register
            </button>
          )
        )}
      </div>

      {/* Mobile Menu Controls */}
      <div className="flex items-center lg:hidden">
        {isLoggedIn && !isCompanyDashboard ? (
          <div className="flex items-center space-x-3 mr-4">
            <UserProfileMenu />
          </div>
        ) : !isLoggedIn ? (
          <button
            className="inline-flex items-center gap-2 mr-2 rounded-full bg-orange-500 px-3 py-1.5 text-white font-semibold tracking-wide hover:bg-orange-600 transition-colors"
            onClick={handleRegisterClick}
          >
            Register
          </button>
        ) : null}
        <button
          className="text-orange-500 text-2xl focus:outline-none"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-label={isOpen ? "Close menu" : "Open menu"}
        >
        <FaBars/>
        </button>
      </div>

      {/* Mobile Drawer */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-80 z-50 transform ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } transition-transform duration-300 ease-in-out lg:hidden`}
        aria-hidden={!isOpen}
      >
        <div className="relative w-3/4 h-full bg-gray-900 text-gray-300 p-6 overflow-y-auto">
          {/* Close Button */}
          <button
            className="text-orange-500 text-2xl absolute top-5 right-5 bg-gray-900 p-2 border border-orange-200"
            onClick={() => setIsOpen(false)}
            aria-label="Close menu"
          >
            <FaTimes className="text-orange-500" />
          </button>

          {/* Mobile Navigation Links */}
          <ul className="flex flex-col space-y-6 mt-12">
            {navLinks.map((link) => (
              <li key={link.path}>
                {link.children ? (
                  <div>
                    <button
                      type="button"
                      className={`inline-flex items-center gap-2 hover:text-orange-500 transition-colors w-full text-left ${
                        isInstaToolsActive ? "text-orange-500" : ""
                      }`}
                      onClick={() => setMobileInstaToolsOpen((prev) => !prev)}
                      aria-expanded={mobileInstaToolsOpen}
                    >
                      {link.label}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform duration-200 ${
                          mobileInstaToolsOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {mobileInstaToolsOpen && (
                      <ul className="mt-3 ml-4 flex flex-col space-y-3">
                        {link.children.map((child) => {
                          const isChildActive = location.pathname === child.path;
                          return (
                            <li key={child.path}>
                              <Link
                                to={child.path}
                                className={`block transition-colors hover:text-orange-500 ${
                                  isChildActive
                                    ? "font-semibold text-orange-500"
                                    : "text-gray-400"
                                }`}
                                onClick={() => {
                                  setIsOpen(false);
                                  setMobileInstaToolsOpen(false);
                                }}
                              >
                                {child.label}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                ) : (
                  <Link
                    to={link.path}
                    className={`hover:text-orange-500 transition-colors ${
                      location.pathname === link.path ? "text-orange-500" : ""
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {link.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>

          {/* Contact Button (Only in Drawer) */}
          <button
            className="mt-6 inline-flex w-auto items-center gap-2 rounded-full bg-orange-500 px-4 py-2 text-white font-semibold tracking-wide hover:bg-orange-600 transition-colors"
            onClick={() => {
              navigate("/contact-details");
              setIsOpen(false);
            }}
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full border border-white/70">
              <PhoneCall className="h-4 w-4" />
            </span>
            Contact Us
          </button>
        </div>
      </div>

      {/* Custom Register Modal */}
      {showRegisterModal && (
        <RegisterModal
          isOpen={showRegisterModal}
          onClose={() => setShowRegisterModal(false)}
          onRegisterAs={handleRegisterAs}
          navigate={navigate}
        />
      )}
    </nav>
  );
};

export default Navbar;
